import React, { useState, useEffect } from "react";
import { getAllCampaigns, getSingleCampaign, createCampaignRequest } from "../../services/Campaigns.ts";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { themes } from '../../theme.ts';
import toast from "react-hot-toast";
import { FaTimes, FaCalendar, FaMoneyBillWave, FaUsers, FaCheck, FaTimes as FaTimesIcon, FaInstagram, FaYoutube, FaTwitter, FaFacebook, FaLinkedin, FaLink, FaSearch } from 'react-icons/fa';

interface Campaign {
  id: number;
  title: string;
  description: string;
  category: string;
  start_date: string;
  end_date: string;
  organization: string;
  image: string;
  banner?: string;
  overview?: string;
  created_at: string;
  deadline: string;
  type?: string | string[]; // type can be string or array
  is_live?: boolean;
}

interface CampaignDetails {
  campaign: {
    id: string;
    title: string;
    overview: string;
    banner: string;
    type: string;
    deadline: string;
    campaign_budget: string;
    budget_left: string;
    individual_budget: string;
    is_live: boolean;
    platforms: string;
    num_applicants_left: number;
    brand_details: {
      title: string;
      description: string;
      logo: string;
      location: string;
      district: string;
      state: string;
      pincode: string;
    };
  };
  tasks: Array<{
    brief: string[];
    requirements: string[];
    guidelines: string[];
    payment_details: string[];
    captions: string[];
  }>;
  rules: Array<{
    dos: string[];
    donts: string[];
  }>;
}

const CampaignList: React.FC = () => {
  const { themeName } = useTheme();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignDetails | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  // Reusable function to get fresh user data
  const getUserData = () => {
    const rawData = localStorage.getItem('user');
    return rawData ? JSON.parse(rawData) : null;
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const userData = getUserData();
      const token = userData?.access;

      if (!token) {
        toast.error("Authentication token not found");
        return;
      }

      const response = await getAllCampaigns(token);
      if (response && response.status === 200) {
        setCampaigns(response.data || []);
        // toast.success(`Loaded ${response.data?.length || 0} campaigns successfully!`);
        //toast.success(`Loaded campaigns successfully!`);

      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Check if campaign is active
  const isCampaignActive = (campaign: Campaign) => {
    const now = new Date();
    const deadline = new Date(campaign.deadline);
    return campaign.is_live && deadline > now;
  };

  // Safe type rendering function
  const renderCampaignTypes = (type: string | string[] | undefined) => {
    if (!type) return <span style={{ color: themes[themeName].colors.textMuted }}>No type specified</span>;

    if (Array.isArray(type)) {
      return type.map((t, i) => (
        <span
          key={i}
          style={{
            backgroundColor: themes[themeName].colors.primary + '22',
            color: themes[themeName].colors.primary,
          }}
          className="inline-block text-xs px-2 py-1 rounded mr-1 mb-1"
        >
          {t}
        </span>
      ));
    }

    if (typeof type === 'string') {
      if (type.includes(',')) {
        return type.split(',').map((t, i) => (
          <span
            key={i}
            style={{
              backgroundColor: themes[themeName].colors.primary + '22',
              color: themes[themeName].colors.primary,
            }}
            className="inline-block text-xs px-2 py-1 rounded mr-1 mb-1"
          >
            {t.trim()}
          </span>
        ));
      }
      return (
        <span
          style={{
            backgroundColor: themes[themeName].colors.primary + '22',
            color: themes[themeName].colors.primary,
          }}
          className="inline-block text-xs px-2 py-1 rounded"
        >
          {type}
        </span>
      );
    }

    return <span style={{ color: themes[themeName].colors.textMuted }}>Unknown type</span>;
  };

  // Filter only active campaigns
  const activeCampaigns = campaigns.filter(campaign => isCampaignActive(campaign));

  // Apply search and category filters to active campaigns only
  const filteredCampaigns = activeCampaigns.filter(
    (campaign) =>
      (category === "all" || campaign.category === category) &&
      campaign.title.toLowerCase().includes(search.toLowerCase())
  );

  const handleViewDetails = async (campaignId: number) => {
    try {
      setDetailsLoading(true);
      const userData = getUserData();
      const token = userData?.access;

      if (!token) {
        toast.error("Authentication token not found");
        return;
      }

      const response = await getSingleCampaign(token, campaignId);
      if (response?.data?.data) {
        setSelectedCampaign(response.data.data);
        setIsModalOpen(true);
      } else {
        toast.error("Failed to load campaign details");
      }
    } catch (err) {
      console.log(err);
      toast.error("Error loading campaign details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCampaign(null);
  };

  const handleApply = async (campaignId: number) => {
    try {
      const userData = getUserData();
      const token = userData?.access;
      const userId = userData?.user?.id;

      if (!token || !userId) {
        toast.error("You must be logged in to apply for a campaign.");
        return;
      }

      // Show loading toast
      const loadingToast = toast.loading("Checking campaign eligibility...");

      // Use the same token for fetching campaign details
      const campaignResponse = await getSingleCampaign(token, campaignId);
      const campaign = campaignResponse?.data?.data?.campaign;

      if (!campaign) {
        toast.dismiss(loadingToast);
        toast.error("Campaign not found.");
        return;
      }

      const now = new Date();
      const deadline = new Date(campaign.deadline);
      if (!campaign.is_live || deadline < now) {
        toast.dismiss(loadingToast);
        toast.error("This campaign is not active or has expired.");
        return;
      }

      // Update loading message
      toast.loading("Submitting your application...", { id: loadingToast });

      const requestData = {
        campaign: campaignId.toString(),
        user: userId,
        status: 'PENDING'
      };

      await createCampaignRequest(requestData);

      // Dismiss loading and show success
      toast.dismiss(loadingToast);
      toast.success("Application submitted successfully!");

      // Close modal if open
      if (isModalOpen) {
        closeModal();
      }

      // Navigate after a short delay so user can see the success message
      setTimeout(() => {
        navigate('/dashboard/campaings/request');
      }, 1500);

    } catch (err: any) {
      console.log(err.message);

      // Dismiss the loading toast first
      toast.dismiss();

      // Check for duplicate request error
      if (err.error === "Duplicate request" ||
        err.message === "You already have a request for this campaign") {
        toast.error("You have already applied to this campaign!");
      } else {
        const errorMessage = err.message || "Failed to apply. Please try again.";
        toast.error(errorMessage);
      }
    }
  };

  // Show toast when search/filter returns no results
  useEffect(() => {
    if (!loading && filteredCampaigns.length === 0 && (search || category !== "all")) {
      toast("No active campaigns match your search criteria", {
        icon: "🔍",
        duration: 3000,
      });
    }
  }, [filteredCampaigns.length, search, category, loading]);

  // Render platform icons
  const renderPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return <FaInstagram className="text-pink-600" />;
      case 'youtube':
        return <FaYoutube className="text-red-600" />;
      case 'twitter':
      case 'x (twitter)':
        return <FaTwitter className="text-blue-400" />;
      case 'facebook':
        return <FaFacebook className="text-blue-600" />;
      case 'linkedin':
        return <FaLinkedin className="text-blue-700" />;
      default:
        return <FaLink className="text-gray-500" />;
    }
  };

  // Parse platforms from string
  const parsePlatforms = (platformsString: string) => {
    try {
      if (platformsString.startsWith('[')) {
        return JSON.parse(platformsString);
      }
      return platformsString.split(',').map(p => p.trim());
    } catch {
      return platformsString.split(',').map(p => p.trim());
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderColor: themes[themeName].colors.primary }}
        ></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen p-6"
      style={{
        backgroundColor: themes[themeName].colors.background,
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header - Improved UI */}
        <div className="text-center mb-8">
          <h1
            className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
            style={{ color: themes[themeName].colors.text }}
          >
            Active Campaigns
          </h1>
          {/* <p className="text-lg mb-6" style={{ color: themes[themeName].colors.textSecondary }}>
            Discover and apply to active campaigns that match your interests
          </p> */}

          {/* Active Campaigns Count */}
          {/* <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
            <FaCheck className="w-4 h-4" />
            {activeCampaigns.length} Active Campaign{activeCampaigns.length !== 1 ? 's' : ''} Available
          </div> */}
        </div>

        {/* Controls - Improved UI */}
        <div
          className="rounded-2xl p-6 mb-8 border shadow-sm"
          style={{
            backgroundColor: themes[themeName].colors.surface,
            borderColor: themes[themeName].colors.border,
          }}
        >
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            {/* Search Box with Border */}
            <div className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search active campaigns by title..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-4 py-3 pl-12 rounded-xl border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{
                    borderColor: themes[themeName].colors.border,
                    backgroundColor: themes[themeName].colors.surface,
                    color: themes[themeName].colors.text,
                  }}
                />
                <FaSearch
                  className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2"
                  style={{ color: themes[themeName].colors.textMuted }}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              {/* Category Filter - Uncomment if needed */}
              {/* <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="px-4 py-3 rounded-lg transition-all duration-300 border-2"
                style={{
                  borderColor: themes[themeName].colors.border,
                  backgroundColor: themes[themeName].colors.surface,
                  color: themes[themeName].colors.text,
                }}
              >
                <option value="all">All Categories</option>
                <option value="fashion">Fashion</option>
                <option value="tech">Tech</option>
                <option value="fitness">Fitness</option>
                <option value="beauty">Beauty</option>
                <option value="lifestyle">Lifestyle</option>
                <option value="food">Food</option>
              </select> */}

              {/* View Mode Toggle */}
              <div
                className="flex rounded-xl overflow-hidden border-2"
                style={{
                  borderColor: themes[themeName].colors.border,
                  backgroundColor: themes[themeName].colors.surface,
                }}
              >
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-3 transition-all duration-300 ${viewMode === "grid"
                      ? "text-white"
                      : "hover:bg-primary/10"
                    }`}
                  style={{
                    backgroundColor: viewMode === "grid" ? themes[themeName].colors.primary : 'transparent',
                    color: viewMode === "grid" ? themes[themeName].colors.surface : themes[themeName].colors.primary,
                  }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h4v4H4zM10 6h4v4h-4zM16 6h4v4h-4zM4 12h4v4H4zM10 12h4v4h-4zM16 12h4v4h-4zM4 18h4v4H4zM10 18h4v4h-4zM16 18h4v4h-4z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-3 transition-all duration-300 ${viewMode === "list"
                      ? "text-white"
                      : "hover:bg-primary/10"
                    }`}
                  style={{
                    backgroundColor: viewMode === "list" ? themes[themeName].colors.primary : 'transparent',
                    color: viewMode === "list" ? themes[themeName].colors.surface : themes[themeName].colors.primary,
                  }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Campaign Cards */}
        {filteredCampaigns.length > 0 ? (
          <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-6"}>
            {filteredCampaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="rounded-xl shadow-lg overflow-hidden border hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                style={{
                  backgroundColor: themes[themeName].colors.surface,
                  borderColor: themes[themeName].colors.border,
                }}
              >
                {/* Campaign Status Badge */}
                <div className="absolute top-3 left-3 z-10">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                    <FaCheck className="w-3 h-3" />
                    Active
                  </span>
                </div>

                {/* Campaign Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={campaign?.banner || "/placeholder.png"}
                    alt={campaign.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div
                    className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: themes[themeName].colors.primary }}
                  >
                    {campaign.category || "General"}
                  </div>
                </div>

                {/* Campaign Content */}
                <div className="p-6">
                  <h3
                    className="text-xl font-bold mb-2 line-clamp-2"
                    style={{ color: themes[themeName].colors.text }}
                  >
                    {campaign.title}
                  </h3>

                  <p
                    className="mb-4 line-clamp-3"
                    style={{ color: themes[themeName].colors.textSecondary }}
                  >
                    {campaign?.overview || campaign.description || "No description available."}
                  </p>

                  {/* Campaign Dates */}
                  <div className="flex items-center text-sm mb-4" style={{ color: themes[themeName].colors.textSecondary }}>
                    <FaCalendar className="w-4 h-4 mr-2" />
                    <span>
                      Deadline: {new Date(campaign?.deadline).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Campaign Types */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">{renderCampaignTypes(campaign.type)}</div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleViewDetails(campaign.id)}
                      className="flex-1 py-2 px-4 rounded-lg transition-all duration-300 font-semibold text-sm border"
                      style={{
                        backgroundColor: 'transparent',
                        color: themes[themeName].colors.primary,
                        borderColor: themes[themeName].colors.primary
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = themes[themeName].colors.primary;
                        e.currentTarget.style.color = themes[themeName].colors.surface;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = themes[themeName].colors.primary;
                      }}
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleApply(campaign.id)}
                      className="flex-1 text-white py-2 px-4 rounded-lg transition-all duration-300 font-semibold text-sm"
                      style={{
                        backgroundColor: themes[themeName].colors.success,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = themes[themeName].colors.success + 'CC';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = themes[themeName].colors.success;
                      }}
                    >
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="text-center py-12 rounded-xl shadow-lg border"
            style={{
              backgroundColor: themes[themeName].colors.surface,
              borderColor: themes[themeName].colors.border,
            }}
          >
            <svg
              className="w-16 h-16 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: themes[themeName].colors.primary }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3
              className="text-xl font-semibold mb-2"
              style={{ color: themes[themeName].colors.text }}
            >
              {activeCampaigns.length === 0 ? "No active campaigns available" : "No campaigns match your search"}
            </h3>
            <p style={{ color: themes[themeName].colors.textSecondary }}>
              {activeCampaigns.length === 0
                ? "Check back later for new campaign opportunities"
                : "Try adjusting your search criteria"}
            </p>
            {activeCampaigns.length === 0 && (
              <button
                onClick={fetchCampaigns}
                className="mt-4 px-6 py-2 rounded-lg transition-all duration-300 font-semibold"
                style={{
                  backgroundColor: themes[themeName].colors.primary,
                  color: themes[themeName].colors.surface,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = themes[themeName].colors.primary + 'CC';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = themes[themeName].colors.primary;
                }}
              >
                Refresh Campaigns
              </button>
            )}
          </div>
        )}

        {/* Results Count */}
        {filteredCampaigns.length > 0 && (
          <div
            className="mt-6 text-center"
            style={{ color: themes[themeName].colors.textSecondary }}
          >
            Showing {filteredCampaigns.length} of {activeCampaigns.length} active campaigns
          </div>
        )}
      </div>

      {/* Campaign Details Modal */}
      {isModalOpen && selectedCampaign && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: themes[themeName].colors.surface, borderColor: themes[themeName].colors.border, borderWidth: 1, borderStyle: 'solid' }}>
            {/* Modal Header */}
            <div className="sticky top-0 p-6 rounded-t-xl flex justify-between items-center border-b" style={{ backgroundColor: themes[themeName].colors.surface, borderColor: themes[themeName].colors.border }}>
              <h2 className="text-2xl font-bold" style={{ color: themes[themeName].colors.text }}>{selectedCampaign.campaign.title}</h2>
              <button
                onClick={closeModal}
                className="transition-colors"
                style={{ color: themes[themeName].colors.textMuted }}
                onMouseEnter={(e) => e.currentTarget.style.color = themes[themeName].colors.text}
                onMouseLeave={(e) => e.currentTarget.style.color = themes[themeName].colors.textMuted}
              >
                <FaTimes size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Campaign Banner */}
              <div className="rounded-lg overflow-hidden">
                <img
                  src={selectedCampaign.campaign.banner}
                  alt={selectedCampaign.campaign.title}
                  className="w-full h-64 object-cover"
                />
              </div>

              {/* Campaign Status */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: themes[themeName].colors.success + '22', color: themes[themeName].colors.success }}>
                    <FaCheck /> Active
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: themes[themeName].colors.primary + '22', color: themes[themeName].colors.primary }}>
                    {selectedCampaign.campaign.type}
                  </span>
                </div>
                <div className="text-sm" style={{ color: themes[themeName].colors.textSecondary }}>
                  <FaUsers className="inline mr-1" />
                  {selectedCampaign.campaign.num_applicants_left} spots left
                </div>
              </div>

              {/* Campaign Overview */}
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: themes[themeName].colors.text }}>Overview</h3>
                <p style={{ color: themes[themeName].colors.textSecondary, lineHeight: '1.625' }}>{selectedCampaign.campaign.overview}</p>
              </div>

              {/* Campaign Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: themes[themeName].colors.surface + '80' }}>
                  <FaCalendar className="mb-2" style={{ color: themes[themeName].colors.primary }} />
                  <h4 className="font-semibold" style={{ color: themes[themeName].colors.text }}>Deadline</h4>
                  <p style={{ color: themes[themeName].colors.textSecondary }}>{new Date(selectedCampaign.campaign.deadline).toLocaleDateString()}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ backgroundColor: themes[themeName].colors.surface + '80' }}>
                  <FaMoneyBillWave className="mb-2" style={{ color: themes[themeName].colors.success }} />
                  <h4 className="font-semibold" style={{ color: themes[themeName].colors.text }}>Individual Budget</h4>
                  <p style={{ color: themes[themeName].colors.textSecondary }}>₹{selectedCampaign.campaign.individual_budget}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ backgroundColor: themes[themeName].colors.surface + '80' }}>
                  <FaMoneyBillWave className="mb-2" style={{ color: themes[themeName].colors.secondary }} />
                  <h4 className="font-semibold" style={{ color: themes[themeName].colors.text }}>Total Budget</h4>
                  <p style={{ color: themes[themeName].colors.textSecondary }}>₹{selectedCampaign.campaign.campaign_budget}</p>
                </div>
              </div>

              {/* Platforms */}
              {selectedCampaign.campaign.platforms && (
                <div>
                  <h3 className="text-lg font-semibold mb-3" style={{ color: themes[themeName].colors.text }}>Platforms</h3>
                  <div className="flex flex-wrap gap-2">
                    {parsePlatforms(selectedCampaign.campaign.platforms).map((platform: string, index: number) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
                        style={{ backgroundColor: themes[themeName].colors.surface + '80', color: themes[themeName].colors.text }}
                      >
                        {renderPlatformIcon(platform)}
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Brand Details */}
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: themes[themeName].colors.text }}>Brand Details</h3>
                <div className="p-4 rounded-lg" style={{ backgroundColor: themes[themeName].colors.surface + '80' }}>
                  <h4 className="font-semibold" style={{ color: themes[themeName].colors.text }}>{selectedCampaign.campaign.brand_details.title}</h4>
                  <p className="mt-1" style={{ color: themes[themeName].colors.textSecondary }}>{selectedCampaign.campaign.brand_details.description}</p>
                  <p className="text-sm mt-2" style={{ color: themes[themeName].colors.textMuted }}>
                    {selectedCampaign.campaign.brand_details.district}, {selectedCampaign.campaign.brand_details.state} - {selectedCampaign.campaign.brand_details.pincode}
                  </p>
                </div>
              </div>

              {/* Tasks */}
              {selectedCampaign.tasks && selectedCampaign.tasks.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3" style={{ color: themes[themeName].colors.text }}>Campaign Brief</h3>
                  {selectedCampaign.tasks.map((task, index) => (
                    <div key={index} className="space-y-4">
                      {task.brief && task.brief.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2" style={{ color: themes[themeName].colors.primary }}>Brief</h4>
                          <ul className="list-disc list-inside space-y-1" style={{ color: themes[themeName].colors.textSecondary }}>
                            {task.brief.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {task.requirements && task.requirements.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2" style={{ color: themes[themeName].colors.primary }}>Requirements</h4>
                          <ul className="list-disc list-inside space-y-1" style={{ color: themes[themeName].colors.textSecondary }}>
                            {task.requirements.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {task.guidelines && task.guidelines.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2" style={{ color: themes[themeName].colors.primary }}>Guidelines</h4>
                          <ul className="list-disc list-inside space-y-1" style={{ color: themes[themeName].colors.textSecondary }}>
                            {task.guidelines.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Rules */}
              {selectedCampaign.rules && selectedCampaign.rules.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedCampaign.rules.map((rule, index) => (
                    <div key={index}>
                      {rule.dos && rule.dos.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2" style={{ color: themes[themeName].colors.success }}>Do's</h4>
                          <ul className="list-disc list-inside space-y-1" style={{ color: themes[themeName].colors.textSecondary }}>
                            {rule.dos.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {rule.donts && rule.donts.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2" style={{ color: themes[themeName].colors.error }}>Don'ts</h4>
                          <ul className="list-disc list-inside space-y-1" style={{ color: themes[themeName].colors.textSecondary }}>
                            {rule.donts.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Payment Details */}
              {selectedCampaign.tasks && selectedCampaign.tasks[0]?.payment_details && selectedCampaign.tasks[0].payment_details.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3" style={{ color: themes[themeName].colors.text }}>Payment Details</h3>
                  <div className="rounded-lg p-4" style={{ backgroundColor: themes[themeName].colors.warning + '22', borderColor: themes[themeName].colors.warning, borderWidth: 1, borderStyle: 'solid' }}>
                    <ul className="list-disc list-inside space-y-1" style={{ color: themes[themeName].colors.text }}>
                      {selectedCampaign.tasks[0].payment_details.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {/* <div className="flex gap-4 pt-6 border-t" style={{ borderColor: themes[themeName].colors.border }}>
                <button
                  onClick={() => handleApply(parseInt(selectedCampaign.campaign.id))}
                  className="flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-300 text-white"
                  style={{
                    backgroundColor: themes[themeName].colors.success,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = themes[themeName].colors.success + 'CC';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = themes[themeName].colors.success;
                  }}
                >
                  Apply Now
                </button>
                <button
                  onClick={closeModal}
                  className="flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-300"
                  style={{
                    backgroundColor: themes[themeName].colors.secondary,
                    color: themes[themeName].colors.inverseText,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = themes[themeName].colors.secondary + 'CC';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = themes[themeName].colors.secondary;
                  }}
                >
                  Close
                </button>
              </div> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignList;